import React, { useMemo } from 'react';
import { X, ArrowRight, Clock } from 'lucide-react';
import { getPromoTheme } from './promoThemes';
import './promoPopup.css';

// Formes SVG des particules par type de décor
const SHAPES = {
  snow: (c) => <circle cx="12" cy="12" r="9" fill={c} opacity="0.9" />,
  sparkle: (c) => <path d="M12 1l2.4 7.6L22 12l-7.6 2.4L12 23l-2.4-8.6L2 12l7.6-3.4z" fill={c} />,
  confetti: (c) => <rect x="4" y="9" width="16" height="6" rx="1.5" fill={c} />,
  hearts: (c) => <path d="M12 21s-7.5-4.6-9.6-9.3C.9 8.2 3 4.5 6.6 4.5c2 0 3.5 1.1 4.4 2.5.9-1.4 2.4-2.5 4.4-2.5 3.6 0 5.7 3.7 4.2 7.2C19.5 16.4 12 21 12 21z" fill={c} />,
  eggs: (c) => <ellipse cx="12" cy="13" rx="7.5" ry="10" fill={c} />,
  petals: (c) => <path d="M12 2c4 4 6 8 6 12a6 6 0 0 1-12 0c0-4 2-8 6-12z" fill={c} opacity="0.95" />,
  bats: (c) => <path d="M2 9c3-1 5 0 6 2 1-2 2-3 4-3s3 1 4 3c1-2 3-3 6-2-2 1-3 3-3 5-2-1-3 0-4 2-1-2-2-3-3-3s-2 1-3 3c-1-2-2-3-4-2 0-2-1-4-3-5z" fill={c} />,
  sun: (c) => <circle cx="12" cy="12" r="8" fill={c} opacity="0.85" />,
  leaves: (c) => <path d="M3 21C3 10 10 3 21 3c0 11-7 18-18 18zm2-2C13 17 17 13 19 5 11 7 7 11 5 19z" fill={c} />,
};

const MODE = {
  snow: 'fall', sparkle: 'twinkle', confetti: 'fall', hearts: 'rise', eggs: 'fall',
  petals: 'flutter', bats: 'flutter', sun: 'twinkle', leaves: 'flutter',
};

const COLORS = {
  snow: ['#FFFFFF', '#EAF4FF', '#D6EBFF'],
  confetti: ['#F5D06F', '#FF6B8B', '#6BD3FF', '#9CFFB0', '#C79BFF'],
  eggs: ['#FFD9E8', '#C8F1FF', '#FFF4B8', '#D8FFD1', '#E8D5FF'],
  petals: ['#FFC9D6', '#FFE1E8', '#FFB3C7'],
  hearts: ['#FF8FAB', '#FFC2D1', '#FF5C8A'],
  leaves: ['#E5A63A', '#C96A2B', '#F2C572'],
};

const rnd = (seed) => {
  let s = seed * 9301 + 49297;
  return () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
};

export const ThemeDecor = ({ themeKey, count = 18 }) => {
  const theme = getPromoTheme(themeKey);
  const type = theme.particle || 'sparkle';
  const particles = useMemo(() => {
    const r = rnd(type.length * 7 + count);
    return Array.from({ length: count }).map((_, i) => ({
      x: `${Math.round(r() * 100)}%`,
      y: `${Math.round(r() * 100)}%`,
      size: `${8 + Math.round(r() * 14)}px`,
      dur: `${6 + r() * 8}s`,
      delay: `${-r() * 10}s`,
      drift: `${Math.round((r() - 0.5) * 120)}px`,
      spin: `${Math.round((r() - 0.5) * 720)}deg`,
      color: (COLORS[type] || [theme.accent])[i % (COLORS[type] || [theme.accent]).length],
    }));
  }, [type, count, theme.accent]);
  const shape = SHAPES[type] || SHAPES.sparkle;
  const mode = MODE[type] || 'fall';
  return (
    <div className="promo-popup-decor" aria-hidden="true" data-testid="promo-popup-decor">
      {particles.map((p, i) => (
        <svg
          key={i}
          viewBox="0 0 24 24"
          className={`promo-particle ${mode !== 'fall' ? `promo-particle--${mode}` : ''}`}
          style={{ '--x': p.x, '--y': p.y, '--size': p.size, '--dur': p.dur, '--delay': p.delay, '--drift': p.drift, '--spin': p.spin }}
        >
          {shape(p.color)}
        </svg>
      ))}
    </div>
  );
};

const fmtPrice = (v) => (v == null || v === '' ? null : `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`);

const UI_TEXT = {
  fr: { until: "Jusqu'au", dismiss: 'Ne plus afficher', close: 'Fermer', cta: 'Découvrir' },
  en: { until: 'Until', dismiss: "Don't show again", close: 'Close', cta: 'Discover' },
  de: { until: 'Bis zum', dismiss: 'Nicht mehr anzeigen', close: 'Schließen', cta: 'Entdecken' },
};

const THEME_LABELS = {
  en: { neutre: 'Golf', noel: 'Christmas', nouvel_an: 'New Year', saint_valentin: "Valentine's Day", paques: 'Easter', fete_des_meres: "Mother's Day", halloween: 'Halloween', ete: 'Summer', rentree: 'Back to school', black_friday: 'Black Friday' },
  de: { neutre: 'Golf', noel: 'Weihnachten', nouvel_an: 'Neujahr', saint_valentin: 'Valentinstag', paques: 'Ostern', fete_des_meres: 'Muttertag', halloween: 'Halloween', ete: 'Sommer', rentree: 'Schulanfang', black_friday: 'Black Friday' },
};

const endsLabel = (iso, lang) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const locale = lang === 'en' ? 'en-GB' : lang === 'de' ? 'de-DE' : 'fr-FR';
  return `${UI_TEXT[lang]?.until || UI_TEXT.fr.until} ${d.toLocaleDateString(locale, { day: 'numeric', month: 'long' })}`;
};

/**
 * Carte visuelle du pop-up (utilisée en live et en aperçu admin).
 * popup = payload de /promo-popup ; imageSrc déjà résolu par l'hôte.
 */
export const PromoPopupCard = ({ popup, imageSrc, onClose, onCta, onDismissForever, preview = false, showDismiss = true }) => {
  const theme = getPromoTheme(popup?.popup_theme);
  const lang = ['en', 'de'].includes(popup?.lang) ? popup.lang : 'fr';
  const ui = UI_TEXT[lang];
  const themeLabel = (THEME_LABELS[lang] || {})[popup?.popup_theme || 'neutre'] || theme.label;
  const title = popup?.title || theme.suggestedTitle;
  const orig = fmtPrice(popup?.original_price);
  const eff = fmtPrice(popup?.effective_price);
  const hasDiscount = orig && eff && Number(popup.original_price) > Number(popup.effective_price);
  const until = endsLabel(popup?.ends_at, lang);
  const showDecor = popup?.popup_show_decor !== false;

  return (
    <div
      className={`promo-popup-card ${preview ? 'promo-popup-card--preview' : ''}`}
      style={{ background: theme.bg, color: theme.text }}
      role="dialog"
      aria-modal={!preview}
      aria-label={title}
      data-testid="promo-popup-card"
    >
      {showDecor && <ThemeDecor themeKey={popup?.popup_theme} />}
      <button type="button" className="promo-popup-close" onClick={onClose} aria-label={ui.close} data-testid="promo-popup-close">
        <X size={18} />
      </button>
      {imageSrc && (
        <>
          <img src={imageSrc} alt="" className="promo-popup-image" data-testid="promo-popup-image" />
          <div className="promo-popup-image-fade" style={{ background: `linear-gradient(180deg, transparent, ${theme.bg.includes('gradient') ? 'rgba(0,0,0,0.0)' : theme.bg})` }} />
        </>
      )}
      <div className="promo-popup-body" style={{ paddingTop: imageSrc ? 18 : 48 }}>
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide uppercase" style={{ background: theme.badgeBg, color: theme.badgeText }} data-testid="promo-popup-badge">
            {themeLabel}
          </span>
          {until && (
            <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: theme.muted }} data-testid="promo-popup-until">
              <Clock size={12} /> {until}
            </span>
          )}
        </div>
        <h3 className="text-2xl font-bold leading-tight mb-1" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: '-0.01em' }} data-testid="promo-popup-title">
          {title}
        </h3>
        {popup?.subtitle && (
          <p className="text-sm font-medium mb-2" style={{ color: theme.accent }} data-testid="promo-popup-subtitle">{popup.subtitle}</p>
        )}
        {popup?.description && (
          <p className="text-sm leading-relaxed mb-4 whitespace-pre-line" style={{ color: theme.muted }} data-testid="promo-popup-description">{popup.description}</p>
        )}
        {(eff || orig) && (
          <div className="flex items-baseline gap-2 mb-4" data-testid="promo-popup-prices">
            {hasDiscount && <span className="text-sm line-through" style={{ color: theme.muted }}>{orig}</span>}
            <span className="text-2xl font-bold" style={{ color: theme.accent }}>{eff || orig}</span>
          </div>
        )}
        {(popup?.popup_url || preview) && (
          <button type="button" className="promo-popup-cta" style={{ background: theme.accent, color: theme.accentText }} onClick={onCta} data-testid="promo-popup-cta">
            {popup?.cta_label || ui.cta} <ArrowRight size={16} />
          </button>
        )}
        {showDismiss && popup?.popup_frequency !== 'once' && (
          <div className="mt-3 text-center">
            <button type="button" className="promo-popup-dismiss" style={{ color: theme.muted }} onClick={onDismissForever} data-testid="promo-popup-dismiss-forever">
              {ui.dismiss}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromoPopupCard;
